import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import ClientPicker from "../ClientPicker";
import { MagnifyingGlass, Hash } from "@phosphor-icons/react";

const CATEGORIES = ["general", "maintenance", "refurbishment", "new_build", "renovation", "fit_out", "planned_works", "reactive_repair", "inspection", "electrical", "plumbing", "roofing", "structural", "mould_eradication", "other"];

/** Convert an inbox email into a new job request (prefilled from the email + matched client). */
export function ConvertEmailDialog({ item, open, onOpenChange, onDone }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setForm({
      title: (item.subject || "").replace(/^(re|fw|fwd):\s*/i, "").slice(0, 200), priority: item.priority || "medium", category: item.ai?.category || "general", property_address: "",
      request_details: item.body_text || "", client_id: item.client_id || "", client_name: item.client_name || item.from_name || "", client_email: item.client_id ? "" : item.from_email,
      client_company: item.client_company || "", client_phone: "", requester_contact_id: item.contact_id || "", requester_contact_name: item.contact_name || "", requester_contact_color: "",
    });
  }, [open, item]);

  if (!form) return null;
  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      const { data } = await API.post(`/inbox/${item.id}/convert`, form);
      toast.success(`Created ${data.reference}${data.ack?.sent ? " — acknowledgement sent" : ""}`);
      onDone?.(data);
      onOpenChange(false);
      navigate(`/job-requests/${data.job_request_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="convert-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">Convert to job request</DialogTitle>
          <DialogDescription>From {item.from_name ? `${item.from_name} <${item.from_email}>` : item.from_email}. Attachments come across automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="mt-1" data-testid="convert-title" /></div>
          <ClientPicker value={form} onChange={setForm} testIdPrefix="convert-client" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={form.priority} onValueChange={v => setForm(p => ({ ...p, priority: v }))}>
                <SelectTrigger className="mt-1" data-testid="convert-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{["low", "medium", "high", "urgent"].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                <SelectTrigger className="mt-1" data-testid="convert-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Property address</Label><Input value={form.property_address} onChange={e => setForm(p => ({ ...p, property_address: e.target.value }))} placeholder="Optional" className="mt-1" data-testid="convert-address" /></div>
          </div>
          <div><Label className="text-xs">Request details</Label><Textarea value={form.request_details} onChange={e => setForm(p => ({ ...p, request_details: e.target.value }))} rows={6} className="mt-1 text-xs" data-testid="convert-details" /></div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="convert-submit">{busy ? "Creating…" : "Create job request"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Add an inbox email (as a note + attachments) to an existing open request, job or emergency job. */
const TARGETS = [
  { key: "job_request", label: "Request", noun: "request", search: (id, q) => API.get(`/inbox/${id}/request-search`, { params: { q } }).then(({ data }) => ({ rows: data.requests, scope: data.scope })) },
  { key: "job", label: "Job", noun: "job", search: (id, q) => API.get(`/inbox/${id}/job-search`, { params: { q, kind: "job" } }).then(({ data }) => ({ rows: data.jobs, scope: data.scope })) },
  { key: "emergency_job", label: "Emergency job", noun: "emergency job", search: (id, q) => API.get(`/inbox/${id}/job-search`, { params: { q, kind: "emergency_job" } }).then(({ data }) => ({ rows: data.jobs, scope: data.scope })) },
];

export function AddToRequestDialog({ item, open, onOpenChange, onDone }) {
  const [kind, setKind] = useState("job_request");
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ rows: [], scope: "" });
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);
  const target = TARGETS.find(t => t.key === kind) || TARGETS[0];

  useEffect(() => {
    if (!open || !item) return;
    const t = setTimeout(() => {
      target.search(item.id, q).then(setRes).catch(() => setRes({ rows: [], scope: "" }));
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [open, item, q, target]);

  useEffect(() => { if (open) { setQ(""); setPicked(""); setKind("job_request"); } }, [open]);

  const submit = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      const { data } = kind === "job_request"
        ? await API.post(`/inbox/${item.id}/add-to-request`, { job_request_id: picked })
        : await API.post(`/inbox/${item.id}/add-to-job`, { entity_type: kind, entity_id: picked });
      toast.success(`Added to ${data.reference || target.noun}`);
      onDone?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="add-to-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">Add to an existing {target.noun}</DialogTitle>
          <DialogDescription>The email text and attachments are appended to the {target.noun}'s notes thread.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-1 rounded-md bg-muted p-0.5 w-fit" data-testid="add-to-kind">
          {TARGETS.map(t => (
            <button key={t.key} type="button" onClick={() => { setKind(t.key); setPicked(""); }}
              className={`px-2.5 h-7 rounded text-xs font-medium transition-colors ${kind === t.key ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`add-to-kind-${t.key}`}>{t.label}</button>
          ))}
        </div>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by reference, title, client or address" className="pl-8 h-9" data-testid="add-to-request-search" />
        </div>
        {res.scope === "client" && !q && <p className="text-[11px] text-muted-foreground">Open {target.noun}s for {item?.client_company || item?.client_name}</p>}
        <div className="max-h-72 overflow-y-auto border border-border rounded-md divide-y divide-border" data-testid="add-to-request-list">
          {res.rows.length === 0 && <p className="text-xs text-muted-foreground p-4 text-center">No open {target.noun}s found</p>}
          {res.rows.map(r => (
            <button key={r.id} type="button" onClick={() => setPicked(r.id)} className={`w-full text-left px-3 py-2 text-xs transition-colors ${picked === r.id ? "bg-primary/10" : "hover:bg-muted/50"}`} data-testid={`add-to-request-option-${r.id}`}>
              <span className="font-mono text-[11px] text-muted-foreground inline-flex items-center gap-1"><Hash size={10} />{r.reference}</span>
              <span className="ml-2 font-medium">{r.title}</span>
              <span className="block text-[11px] text-muted-foreground truncate">{[r.client_company || r.client_name, r.property_address || r.address, r.status?.replace(/_/g, " ")].filter(Boolean).join(" · ")}</span>
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!picked || busy} data-testid="add-to-request-submit">{busy ? "Adding…" : `Add email to ${target.noun}`}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
